import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Map } from './reactMapGl';
import { loadMapbox, MapboxModule } from './loadMapbox';
import { useConfig } from '../../providers/ConfigProvider';
import { useLocationInternal } from '../../hooks/useLocation';

type MapLibState =
  | { status: 'loading' }
  | { status: 'ready'; lib: MapboxModule }
  | { status: 'failed' };

interface Props {
  id?: string;
  children: ReactNode;
  zoom?: number;
  onZoom?: (newZoom: number) => void;
  onDragStart?: () => void;
}
const NeighborhoodMap: FC<Props> = ({
  id,
  children,
  zoom = 15,
  onZoom,
  onDragStart,
}) => {
  const {
    apis: {
      mapbox: { accessToken },
    },
  } = useConfig();

  const { data: location } = useLocationInternal();
  const coordinates = useMemo(() => location?.coordinate, [location]);

  const [mapLib, setMapLib] = useState<MapLibState>({ status: 'loading' });
  useEffect(() => {
    let mounted = true;
    loadMapbox().then(
      (lib) => {
        if (mounted) setMapLib({ status: 'ready', lib });
      },
      () => {
        if (mounted) setMapLib({ status: 'failed' });
      },
    );
    return () => {
      mounted = false;
    };
  }, []);

  if (mapLib.status !== 'ready') {
    // Same box as the map; the next mount loads again after a failure.
    return (
      <div
        id={id}
        className="flex h-full w-full items-center justify-center bg-gray-100"
      >
        {mapLib.status === 'failed' ? (
          <span className="body1 text-gray">
            <FormattedMessage defaultMessage="Kartan kunde inte laddas" />
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <Map
      id={id}
      mapLib={mapLib.lib}
      initialViewState={{
        latitude: coordinates?.y,
        longitude: coordinates?.x,
        zoom: zoom,
      }}
      style={{ width: '100%', height: '100%' }}
      mapboxAccessToken={accessToken}
      mapStyle="mapbox://styles/mdjarv/cjte78cxa1wb31fo7xyb90ib9"
      onZoom={(e) => onZoom?.(e.viewState.zoom)}
      onDragStart={onDragStart}
    >
      {children}
    </Map>
  );
};

export default NeighborhoodMap;
